/**
 * API Configuration
 * Centralized configuration for API endpoints and base URLs
 */

// Get base URL from environment variable or use default
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api/v1';

// Get WebSocket base URL from environment variable or use default
export const WS_BASE_URL = process.env.REACT_APP_WS_URL || 'ws://localhost:8080/api/v1/websocket';

// Debug logging to verify URLs
console.log('🔧 API Configuration:');
console.log('  API_BASE_URL:', API_BASE_URL);
console.log('  WS_BASE_URL:', WS_BASE_URL);
console.log('  Environment Variables:');
console.log('    REACT_APP_API_BASE_URL:', process.env.REACT_APP_API_BASE_URL);
console.log('    REACT_APP_WS_URL:', process.env.REACT_APP_WS_URL);
console.log('  All process.env keys starting with REACT_APP_:', Object.keys(process.env).filter(key => key.startsWith('REACT_APP_')));

// Base WebSocket URL for components that need just the base URL
export const WS_BASE_URL_ONLY = WS_BASE_URL.replace('/exam', '').replace(/\/[^\/]*\/?$/, '');

// API Endpoints
export const API_ENDPOINTS = {
  // Assessment endpoints (using existing working endpoints)
  ASSESSMENTS: `${API_BASE_URL}/language-tests/assessments`,
  ASSESSMENT_BY_ID: (id: string) => `${API_BASE_URL}/language-tests/assessments/${id}`,
  CREATE_ASSESSMENT: `${API_BASE_URL}/language-tests/assessments`,
  
  // Tenant access endpoints (using existing working endpoints)
  TENANT_ACCESS: `${API_BASE_URL}/language-tests/tenant-access`,
  TENANT_ACCESS_BY_ID: (id: string) => `${API_BASE_URL}/language-tests/tenant-access/${id}`,
  
  // Sections and Subsections endpoints
  SECTIONS: `${API_BASE_URL}/language-tests/sections`,
  CREATE_SECTION: `${API_BASE_URL}/language-tests/sections`,
  UPDATE_SECTION: (id: string) => `${API_BASE_URL}/language-tests/sections/${id}`,
  DELETE_SECTION: (id: string) => `${API_BASE_URL}/language-tests/sections/${id}`,
  
  SUBSECTIONS: `${API_BASE_URL}/language-tests/sub-sections`,
  CREATE_SUBSECTION: `${API_BASE_URL}/language-tests/sub-sections`,
  UPDATE_SUBSECTION: (id: string) => `${API_BASE_URL}/language-tests/sub-sections/${id}`,
  DELETE_SUBSECTION: (id: string) => `${API_BASE_URL}/language-tests/sub-sections/${id}`,
  SUBSECTIONS_BY_SECTION: (sectionId: string) => `${API_BASE_URL}/language-tests/sub-sections?section_id=${sectionId}`,
  
  // Questions endpoints
  QUESTIONS: `${API_BASE_URL}/language-tests/questions`,
  CREATE_QUESTION: `${API_BASE_URL}/language-tests/questions`,
  UPDATE_QUESTION: (id: string) => `${API_BASE_URL}/language-tests/questions/${id}`,
  DELETE_QUESTION: (id: string) => `${API_BASE_URL}/language-tests/questions/${id}`,
  PARENT_QUESTIONS: `${API_BASE_URL}/language-tests/parent-questions`,
  PARENT_QUESTIONS_BY_SECTION: (sectionId?: string, subSectionId?: string) => {
    const params = new URLSearchParams();
    if (sectionId) params.append('section_id', sectionId);
    if (subSectionId) params.append('sub_section_id', subSectionId);
    return `${API_BASE_URL}/language-tests/parent-questions?${params.toString()}`;
  },
  QUESTIONS_WITH_FILTERS: (params: any) => {
    const searchParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        searchParams.append(key, params[key]);
      }
    });
    return `${API_BASE_URL}/language-tests/questions?${searchParams.toString()}`;
  },
  
  // Exam endpoints
  START_ASSIGNMENT: (assignmentId: string) => `${API_BASE_URL}/language-tests/assignments/${assignmentId}/start`,
  EXAM_QUESTIONS: `${API_BASE_URL}/exam-questions`,
  GENERATE_QUESTIONS: (assignmentId: string) => `${API_BASE_URL}/exam-questions/generate/${assignmentId}`,
  ASSIGNMENT_QUESTIONS: (assignmentId: string) => `${API_BASE_URL}/exam-questions/${assignmentId}/list`,
  SPECIFIC_QUESTION: (assignmentId: string, questionNumber: number) => `${API_BASE_URL}/exam-questions/${assignmentId}/question/${questionNumber}`,
  SUBMIT_ANSWER: (assignmentId: string) => `${API_BASE_URL}/exam-questions/${assignmentId}/answer`,
  SUBMIT_AUDIO_ANSWER: (assignmentId: string) => `${API_BASE_URL}/exam-questions/${assignmentId}/audio-answer`,
  ASSIGNMENT_SUMMARY: (assignmentId: string) => `${API_BASE_URL}/exam-questions/${assignmentId}/summary`,
  
  // WebSocket endpoints
  WEBSOCKET_EXAM: (assignmentId: string) => `${WS_BASE_URL}/exam/${assignmentId}`,
  
  // Audio upload endpoints
  AUDIO_UPLOAD: `${API_BASE_URL}/audio/upload-audio`,
  AUDIO_HEALTH: `${API_BASE_URL}/audio/health`,
  
  // Scoring endpoints
  SCORE_ASSIGNMENT: (assignmentId: string) => `${API_BASE_URL}/scoring/assignment/${assignmentId}`,
  SCORE_ASSIGNMENT_ASYNC: (assignmentId: string) => `${API_BASE_URL}/scoring/assignment/${assignmentId}/async`,
  GET_ASSIGNMENT_RESULTS_SIMPLE: (assignmentId: string) => `${API_BASE_URL}/scoring/assignment/${assignmentId}/results-simple`,
  SCORE_QUESTION: (assignmentQuestionId: string) => `${API_BASE_URL}/scoring/question/${assignmentQuestionId}`,
  GET_TASK_STATUS: (taskId: string) => `${API_BASE_URL}/scoring/task/${taskId}/status`,
  
  // Assignment Questions for detailed results (legacy)
  ASSIGNMENT_QUESTIONS_DETAILED: (assignmentId: string) => `${API_BASE_URL}/assignment-questions/${assignmentId}/detailed`,

  // Assignment endpoints (using existing working endpoints)
  ASSIGNMENTS: `${API_BASE_URL}/language-tests/assignments`,
  ASSIGNMENT_BY_ID: (id: string) => `${API_BASE_URL}/language-tests/assignments/${id}`,
  CREATE_ASSIGNMENT: `${API_BASE_URL}/language-tests/assignments`,
  
  // Assignment Questions endpoints
  ASSIGNMENT_QUESTIONS_BASE: `${API_BASE_URL}/assignment-questions`,
  GENERATE_ASSIGNMENT_QUESTIONS: (assignmentId: string) => `${API_BASE_URL}/assignment-questions/generate/${assignmentId}`,
  GENERATE_QUESTIONS_WITH_CONFIG: `${API_BASE_URL}/assignment-questions/generate-with-config`,
  GET_ASSIGNMENT_QUESTIONS: (assignmentId: string) => `${API_BASE_URL}/assignment-questions/${assignmentId}`,
  START_ASSIGNMENT_QUESTIONS: (assignmentId: string) => `${API_BASE_URL}/assignment-questions/${assignmentId}/start`,
  
  // Assessment generator endpoints
  ASSESSMENT_GENERATOR: {
    GENERATE: (assessmentId: string) => `${API_BASE_URL}/assessment-generator/generate/${assessmentId}`,
    PREVIEW: (assessmentId: string) => `${API_BASE_URL}/assessment-generator/preview/${assessmentId}`,
    STATS: (assessmentId: string) => `${API_BASE_URL}/assessment-generator/stats/${assessmentId}`,
  },
  
  // Health check
  HEALTH: `${API_BASE_URL}/health`,
};

/**
 * API utility functions
 */
export const apiUtils = {
  /**
   * Create a fetch request with default headers
   */
  createRequest: (method: string, body?: any) => ({
    method,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    ...(body && { body: JSON.stringify(body) }),
  }),

  /**
   * Handle API response
   */
  handleResponse: async (response: Response) => {
    const responseText = await response.text();
    
    if (!response.ok) {
      console.error(`API Error ${response.status}:`, responseText);
      throw new Error(`API Error ${response.status}: ${responseText}`);
    }
    
    try {
      return JSON.parse(responseText);
    } catch (e) {
      console.error('JSON Parse Error:', e, 'Response:', responseText);
      throw new Error('Invalid JSON response from server');
    }
  },

  /**
   * Make a GET request
   */
  get: async (url: string) => {
    const response = await fetch(url, apiUtils.createRequest('GET'));
    return apiUtils.handleResponse(response);
  },

  /**
   * Make a POST request
   */
  post: async (url: string, body: any) => {
    const response = await fetch(url, apiUtils.createRequest('POST', body));
    return apiUtils.handleResponse(response);
  },

  /**
   * Make a PUT request
   */
  put: async (url: string, body: any) => {
    const response = await fetch(url, apiUtils.createRequest('PUT', body));
    return apiUtils.handleResponse(response);
  },

  /**
   * Make a DELETE request
   */
  delete: async (url: string) => {
    const response = await fetch(url, apiUtils.createRequest('DELETE'));
    return apiUtils.handleResponse(response);
  },
};

// Export default configuration
const apiConfig = {
  API_BASE_URL,
  WS_BASE_URL,
  API_ENDPOINTS,
  apiUtils,
};

export default apiConfig;
